import { useEffect, useState } from "react";
import axios from "axios";
import { API, formatINR } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell } from "recharts";

const BRONZE = "#B8895C";
const IVORY = "#EDE4D3";
const META = "#8A8680";
const HAIRLINE = "#2A2825";

const KPI = ({ label, value, sub }) => (
  <div className="p-8" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
    <div className="font-micro text-[#8A8680] mb-3">{label}</div>
    <div className="font-serif text-[#EDE4D3] text-4xl">{value}</div>
    {sub && <div className="font-micro text-[#B8895C] mt-2">{sub}</div>}
  </div>
);

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/analytics`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-[#8A8680] font-micro">Loading analytics…</div>;

  const k = data.kpis;
  const funnelArr = [
    { name: "Visit", value: k.visits || data.funnel.visit },
    { name: "PDP", value: data.funnel.pdp_view },
    { name: "Cart", value: data.funnel.add_to_cart },
    { name: "Checkout", value: data.funnel.checkout_start },
    { name: "Paid", value: data.funnel.order_paid },
  ];

  return (
    <div className="fade-in space-y-10" data-testid="admin-analytics-page">
      <div>
        <div className="font-micro text-[#8A8680] mb-3">Overview</div>
        <h1 className="font-serif text-[#EDE4D3] text-5xl">Analytics.</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-testid="admin-kpi-grid">
        <KPI label="Visits" value={k.visits} />
        <KPI label="Uniques" value={k.unique_visitors} />
        <KPI label="Orders" value={k.orders} />
        <KPI label="Revenue" value={formatINR(k.revenue)} />
        <KPI label="AOV" value={formatINR(k.aov || 0)} />
        <KPI label="Conv." value={`${(k.conversion_rate || 0).toFixed(2)}%`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="p-8" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
          <div className="font-micro text-[#8A8680] mb-6">Traffic · Last 30 days</div>
          {data.traffic.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-[#8A8680] font-micro">No traffic yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.traffic}>
                <CartesianGrid stroke={HAIRLINE} strokeDasharray="1 3" />
                <XAxis dataKey="date" stroke={META} tick={{ fill: META, fontSize: 11 }} />
                <YAxis stroke={META} tick={{ fill: META, fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0B0B0C", border: `1px solid ${HAIRLINE}`, color: IVORY }} />
                <Line type="monotone" dataKey="count" stroke={BRONZE} strokeWidth={1} dot={{ fill: BRONZE, r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="p-8" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
          <div className="font-micro text-[#8A8680] mb-6">Funnel</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={funnelArr} layout="vertical">
              <CartesianGrid stroke={HAIRLINE} strokeDasharray="1 3" />
              <XAxis type="number" stroke={META} tick={{ fill: META, fontSize: 11 }} />
              <YAxis type="category" dataKey="name" stroke={META} tick={{ fill: META, fontSize: 11 }} width={90} />
              <Tooltip contentStyle={{ background: "#0B0B0C", border: `1px solid ${HAIRLINE}`, color: IVORY }} />
              <Bar dataKey="value" fill={BRONZE} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="p-8" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
          <div className="font-micro text-[#8A8680] mb-6">Top Products · Views</div>
          <div className="space-y-4">
            {data.top_by_views.length === 0 && <div className="font-micro text-[#8A8680]">No views yet</div>}
            {data.top_by_views.map((p, i) => (
              <div key={p.slug} className="flex items-center justify-between py-3" style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
                <div>
                  <div className="font-serif text-[#EDE4D3]">{p.name}</div>
                  <div className="font-micro text-[#8A8680] mt-1">{p.category}</div>
                </div>
                <div className="font-serif text-[#B8895C] text-xl">{p.views}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-8" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
          <div className="font-micro text-[#8A8680] mb-6">Category Performance</div>
          {data.category_performance.length === 0 ? (
            <div className="font-micro text-[#8A8680]">No paid orders yet.</div>
          ) : (
            <div className="space-y-4">
              {data.category_performance.map((c) => (
                <div key={c.category} className="flex justify-between py-3" style={{ borderTop: "1px solid var(--hairline)" }}>
                  <div className="font-serif text-[#EDE4D3]">{c.category}</div>
                  <div className="font-serif text-[#B8895C]">{formatINR(c.revenue)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="p-8" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
        <div className="font-micro text-[#8A8680] mb-6">Leads by Source</div>
        {data.leads_by_source.length === 0 ? (
          <div className="font-micro text-[#8A8680]">No leads yet.</div>
        ) : (
          <div className="flex flex-wrap gap-6">
            {data.leads_by_source.map((s) => (
              <div key={s.source || "—"} className="px-6 py-4" style={{ border: "1px solid var(--hairline)" }}>
                <div className="font-micro text-[#8A8680] mb-2">{s.source || "unknown"}</div>
                <div className="font-serif text-[#B8895C] text-2xl">{s.count}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
